import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
 
  Popover,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Formik, Form } from 'formik';
import DeleteField from '../../otherComponent/DeleteStep';
import DynamicFieldSettings from './shared/DynamicFieldSettings';
import Reportform from '../../Reportform';

interface DateComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  selectedNodeData?: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function DateComponent({ values, index, setFieldValue, selectedNodeData, onDeleteField, toSentenceCase, fieldTypeSelector, isSettingsOpen, onSettingsOpen, onSettingsClose }: DateComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);
  // The settings popover renders inside its own Formik, whose render prop
  // shadows `setFieldValue`. Keep a handle on the step form's one.
  const outerSetFieldValue = setFieldValue;

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);
  const open = Boolean(anchorEl);
  const id = open ? 'edit-date-popover' : undefined;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };
  const handleClose = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const field = values.fields[index];
  // const label = field.label || field.question || 'Select Date';
  const value = field.value || null;

  const isThisFieldUnique = field.settings?.isUniqueCode;
  const otherUniqueExists = values.fields.some((f: any, i: number) => f.settings?.isUniqueCode && i !== index);

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: 0
              }}
            >
            <Typography
              variant="body1"
              sx={{ minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {toSentenceCase(values.fields[index].label || values.fields[index].question || 'Select Date')}
            </Typography>
              <CalendarMonthIcon fontSize="small" sx={{ color: 'gray' }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                ref={settingsButtonRef}
                sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                onClick={handleOpen}
              >
                <SettingsIcon fontSize="medium" />
              </Box>
              <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box mt={1} hidden>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={value}
            onChange={(newDate) => setFieldValue(`fields.${index}.value`, newDate)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </LocalizationProvider>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ 
          sx: { 
            width: 350, 
            p: 2, 
            maxHeight: '80vh', 
            overflowY: 'auto',
            overflowX: 'hidden'
          } 
        }}
      >
        <Formik
          initialValues={{
            label: field.label || field.question || '',
            isTimeRequired: field.settings?.isTimeRequired || false,
            includeTimezone: field.settings?.includeTimezone !== false, // default to true
            isStartDate: field.settings?.isStartDate || false,
            isDueDate: field.settings?.isDueDate || false,
            isSupportFileRequired: field.settings?.isSupportFileRequired || false,
            noOfSupportImages: field.settings?.noOfSupportImages || '',
            isUniqueCode: field.settings?.isUniqueCode || false,
            required: field.settings?.required || false,
            // Tracked by this popover's own Formik because Reportform resolves
            // `useField` against the nearest context, which is this one.
            report: field.settings?.report || { enabled: false, uid: null, name: null }
          }}
          enableReinitialize
          onSubmit={(formValues) => {
            const updatedField = {
              ...field,
              question: formValues.label,
              label: formValues.label,
              settings: {
                ...field.settings,
                isTimeRequired: formValues.isTimeRequired,
                includeTimezone: formValues.includeTimezone,
                isStartDate: formValues.isStartDate,
                isDueDate: formValues.isDueDate,
                isSupportFileRequired: formValues.isSupportFileRequired,
                noOfSupportImages: formValues.isSupportFileRequired
                  ? formValues.noOfSupportImages
                  : '',
                isUniqueCode: formValues.isUniqueCode,
                required: formValues.isUniqueCode ? true : formValues.required,
                report: formValues.report
              }
            };
            setFieldValue(`fields.${index}`, updatedField);
            handleClose();
          }}
        >
          {({ values: formValues, handleChange, setFieldValue }) => (
            <Form>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
                <Typography variant="h6" fontWeight="bold">Date Settings</Typography>
                {fieldTypeSelector ? (
                  <Box sx={{ minWidth: 120 }}>
                    {fieldTypeSelector}
                  </Box>
                ) : null}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
            <TextField
            fullWidth
            label="Label"
            name="label"
            value={formValues.label}
            onChange={handleChange}
            size="small"
            placeholder="Enter label"
          />
                </Grid>

                {/* Add this block for Required */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="required"
                        checked={formValues.required}
                        onChange={handleChange}
                      />
                    }
                    label="Required"
                    sx={{ ml: -1.5, mt: -1 }}
                  />
                </Grid>
                {/* End Required block */}

                <Grid item xs={12} sx={{ mt: -4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isTimeRequired"
                        checked={formValues.isTimeRequired}
                        onChange={handleChange}
                      />
                    }
                    label="Time Required"
                    sx={{ ml: -1.5,   }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: -4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="includeTimezone"
                        checked={formValues.includeTimezone}
                        onChange={handleChange}
                      />
                    }
                    label="Include Timezone"
                    sx={{ ml: -1.5,   }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: -4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isStartDate"
                        checked={formValues.isStartDate}
                        onChange={handleChange}
                      />
                    }
                    label="Start Date"
                    sx={{ ml: -1.5,  }}
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: -4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isDueDate"
                        checked={formValues.isDueDate}
                        onChange={handleChange}
                      />
                    }
                    label="Due Date"
                    sx={{ ml: -1.5,   }}
                  />
                </Grid>

                {/* <Grid item xs={12} sx={{ mt: -4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isSupportFileRequired"
                        checked={formValues.isSupportFileRequired}
                        onChange={handleChange}
                      />
                    }
                    label="Support File Required"
                    sx={{ ml: -1.5, }}
                  />
                </Grid> */}

                {formValues.isSupportFileRequired && (
                  <Grid item xs={12} sx={{ mt: -2 }}>
                    <TextField
                      fullWidth
                      name="noOfSupportImages"
                      type="number"
                      label="Number of Images"
                      value={formValues.noOfSupportImages || ''}
                      onChange={handleChange}
                      size="small"
                      inputProps={{ min: 1 }}
                 
                    />
                  </Grid>
                )}

                <Grid item xs={12} sx={{ mt: -4 }}>
                  {!otherUniqueExists || isThisFieldUnique ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isUniqueCode"
                          checked={formValues.isUniqueCode}
                          onChange={e => {
                            setFieldValue('isUniqueCode', e.target.checked);
                            if (e.target.checked) {
                              setFieldValue('required', true);
                            }
                          }}
                        />
                      }
                      label="Unique Code"
                      sx={{ ml: -1.5,   }}
                    />
                  ) : null}
                </Grid>

                {/*
                  Writes straight to the step form via the outer setFieldValue —
                  this popover's own Formik only tracks the fields listed in its
                  initialValues, and its submit spreads `...field.settings`, so
                  the mapping is preserved on Save.
                */}
                <DynamicFieldSettings
                  values={values}
                  index={index}
                  setFieldValue={outerSetFieldValue}
                  open={Boolean(anchorEl)}
                  helperText="Reuse a date entered in an earlier step. The source field must be a date."
                />

                <Grid item xs={12} sx={{ mt: -3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="report.enabled"
                        checked={formValues.report?.enabled || false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFieldValue(
                            'report',
                            isChecked
                              ? { ...(formValues.report || {}), enabled: true }
                              : { enabled: false, uid: null, name: null }
                          );
                        }}
                      />
                    }
                    label="Report "
                    sx={{ ml: -1.5 }}
                  />
                </Grid>

                {formValues.report?.enabled && (
                  <Grid item xs={12} sx={{ mt: -2 }}>
                    <Reportform name="report" />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}

